import { gql } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { saveAs } from 'file-saver';
import type { GraphQLFormattedError } from 'graphql';
import sanitize from 'sanitize-filename';
import type * as Apollo from 'src/gql/generated/apollo-types';
import { DocumentMetadataImportStatus } from 'src/gql/generated/apollo-types';

export const DEFAULT_DISPLAY_NAME = '';
export const FIRST_STEP = 0;
export const SPREADSHEET_MIME_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const PREVIEW_STEP = 2;
export const UPLOAD_STEP = 1;

export const DISPLAY_NAME_FRAGMENT = gql`
  fragment DisplayNameUpdate on DocumentMetadataImport {
    displayName
  }
`;

export const IMPORT_CONFIRM_VALIDATION_ERROR_CODES = [
  'IMPORT_INVALID_CONFIRM_STATE',
  'IMPORT_ORG_MUTEX_BUSY',
] as const;

export interface WizardResumeState {
  activeStep: number;
  displayName: string;
  draftImportId: string | null;
  generatedTemplateQuestionIds: string[] | null;
  overwriteQuestionIds: Set<string>;
  selectedQuestionIds: string[];
  uploadedFile: File | null;
  uploadResult: Apollo.DocumentMetadataImportFieldsFragment | null;
}

export function getWizardResumeState(
  existingImport?: Apollo.DocumentMetadataImportFieldsFragment | null
): WizardResumeState {
  if (!existingImport) {
    return {
      activeStep: FIRST_STEP,
      displayName: DEFAULT_DISPLAY_NAME,
      draftImportId: null,
      generatedTemplateQuestionIds: null,
      overwriteQuestionIds: new Set(),
      selectedQuestionIds: [],
      uploadResult: null,
      uploadedFile: null,
    };
  }

  const isDraft = existingImport.status === DocumentMetadataImportStatus.Draft;
  const isResumableImport =
    existingImport.status === DocumentMetadataImportStatus.ReadyToImport ||
    existingImport.status === DocumentMetadataImportStatus.Failed;
  const hasPriorValidation = isDraft && Boolean(existingImport.summary);
  const questionIds = existingImport.questions.map(question => question.id);

  return {
    activeStep: hasPriorValidation ? PREVIEW_STEP : isDraft ? UPLOAD_STEP : PREVIEW_STEP,
    displayName: existingImport.displayName,
    draftImportId: existingImport.id,
    generatedTemplateQuestionIds: questionIds,
    overwriteQuestionIds: new Set(),
    selectedQuestionIds: questionIds,
    uploadResult: isResumableImport || hasPriorValidation || !isDraft ? existingImport : null,
    uploadedFile:
      (!isDraft || hasPriorValidation) && existingImport.sourceFileName
        ? new File([], existingImport.sourceFileName, { type: SPREADSHEET_MIME_TYPE })
        : null,
  };
}

export function getQuestionIdsMatch(options: {
  generatedQuestionIds: string[] | null;
  selectedQuestionIds: string[];
}): boolean {
  const { generatedQuestionIds, selectedQuestionIds } = options;
  if (!generatedQuestionIds) return false;
  if (generatedQuestionIds.length !== selectedQuestionIds.length) return false;

  return generatedQuestionIds.every(
    (questionId, index) => questionId === selectedQuestionIds[index]
  );
}

export function downloadSpreadsheetFile(options: { fileContent: string; fileName: string }): void {
  const { fileContent, fileName } = options;
  const blob = new Blob([base64ToArrayBuffer(fileContent)], {
    type: SPREADSHEET_MIME_TYPE,
  });
  saveAs(blob, sanitize(fileName));
}

export function getEmptyColumnsWarningMessage(emptyColumns: string[]): string {
  if (emptyColumns.length === 1) {
    return `1 column has no data and will be skipped: ${emptyColumns[0]}`;
  }

  return `${emptyColumns.length} columns have no data and will be skipped: ${emptyColumns.join(
    ', '
  )}`;
}

function base64ToArrayBuffer(fileContent: string): ArrayBuffer {
  const byteCharacters = atob(fileContent);
  const buffer = new ArrayBuffer(byteCharacters.length);
  const bytes = new Uint8Array(buffer);

  for (let i = 0; i < byteCharacters.length; i++) {
    bytes[i] = byteCharacters.charCodeAt(i);
  }

  return buffer;
}

export function getCanConfirmImportUpload(
  importRecord: Pick<Apollo.DocumentMetadataImportFieldsFragment, 'status'>
): boolean {
  return (
    importRecord.status === DocumentMetadataImportStatus.ReadyToImport ||
    importRecord.status === DocumentMetadataImportStatus.Failed
  );
}

export function getDefaultOverwriteQuestionIds(
  preview: Apollo.DocumentMetadataImportPreviewFieldsFragment | null | undefined
): Set<string> {
  const overwriteQuestionIds = (preview?.columns ?? [])
    .map(column => (column.canOverwrite ? column.questionId : null))
    .filter((questionId): questionId is string => Boolean(questionId));

  return new Set(overwriteQuestionIds);
}

/**
 * Optimistic payload for the confirm mutation: the record flips to the same
 * client-only "confirm pending" shape the grid renders as Loading (Validating +
 * no startedAt). Apollo rolls this back automatically if the mutation errors.
 */
export function buildOptimisticConfirmResponse(
  uploadResult: Apollo.DocumentMetadataImportFieldsFragment
): Apollo.ConfirmMetadataImportMutation {
  return {
    __typename: 'Mutation',
    confirmMetadataImport: {
      __typename: 'MetadataImportOperationResult',
      documentMetadataImport: {
        ...uploadResult,
        failedReason: null,
        finishedAt: null,
        startedAt: null,
        status: DocumentMetadataImportStatus.Validating,
        updatedAt: new Date().toISOString(),
      },
    },
  };
}

function getConfirmImportGraphQLErrors(error: unknown): GraphQLFormattedError[] {
  if (CombinedGraphQLErrors.is(error)) return [...error.errors];

  if (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    CombinedGraphQLErrors.is(error.error)
  ) {
    return [...error.error.errors];
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'graphQLErrors' in error &&
    Array.isArray(error.graphQLErrors)
  ) {
    return error.graphQLErrors;
  }

  return [];
}

export function getConfirmImportErrorMessage(error: unknown): Error {
  const graphQLErrors = getConfirmImportGraphQLErrors(error);
  const firstError = graphQLErrors[0];
  if (firstError?.message) return new Error(firstError.message);

  if (error instanceof Error && error.message) return error;

  return new Error('Failed to start import.');
}

export function getIsImportConfirmValidationError(error: unknown): boolean {
  return getConfirmImportGraphQLErrors(error).some(graphQLError => {
    if (!graphQLError.extensions || !('code' in graphQLError.extensions)) return false;

    const code = graphQLError.extensions.code;
    return (
      typeof code === 'string' &&
      IMPORT_CONFIRM_VALIDATION_ERROR_CODES.some(validationCode => validationCode === code)
    );
  });
}
